import moment from 'moment'
import 'moment/locale/ru'
import { declOfNum } from '@/utils'
import {
    CatalogService,
    DayKey,
    HotWindow,
    HotWindowCandidate,
    HotWindowService,
    HotWindowsData,
    HotWindowsFilters,
    HotWindowsTaskType,
} from './types'

moment.locale('ru')

export const DATE_FORMAT = 'YYYY-MM-DD'

export function formatMoney(value: number): string {
    return `${new Intl.NumberFormat('ru-RU').format(Math.round(Number(value) || 0))} ₽`
}

/** «45 мин», «1 ч 30 мин», «4 ч» — как `dur()` в макете. */
export function formatDuration(minutes: number): string {
    const total = Math.max(0, Math.round(Number(minutes) || 0))
    const hours = Math.floor(total / 60)
    const rest = total % 60
    if (hours && rest) return `${hours} ч ${rest} мин`
    if (hours) return `${hours} ч`
    return `${rest} мин`
}

export function plural(count: number, one: string, few: string, many: string): string {
    return declOfNum(count, [one, few, many])
}

export function candidatesLabel(count: number): string {
    return `${count} ${plural(count, 'кандидат', 'кандидата', 'кандидатов')}`
}

export function visitsLabel(count: number): string {
    return `${count} ${plural(count, 'визит', 'визита', 'визитов')}`
}

/** «Понедельник, 27 июля» — подзаголовок списка окон. */
export function formatLongDate(date: string): string {
    const parsed = moment(date, DATE_FORMAT)
    if (!parsed.isValid()) return date
    const weekday = parsed.format('dddd')
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${parsed.format('D MMMM')}`
}

/** «27 июля» — короткая дата в карточке дня. */
export function formatShortDate(date: string): string {
    const parsed = moment(date, DATE_FORMAT)
    return parsed.isValid() ? parsed.format('D MMMM') : date
}

/** «27 июля 2026» — дата в карточке клиента. */
export function formatFullDate(date: string | null): string {
    if (!date) return '—'
    const parsed = moment(date, moment.ISO_8601)
    return parsed.isValid() ? parsed.format('D MMMM YYYY') : String(date)
}

/** «27 июля 2026, 12:10» — строка ленты событий. */
export function formatDateTime(date: string | null): string {
    if (!date) return ''
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format('D MMMM YYYY, HH:mm') : String(date)
}

/** «Обновлено: 09:30» — как в шапке макета v12. */
export function formatUpdatedAt(value?: string | number | null): string {
    if (!value) return ''
    const parsed = moment(value)
    return parsed.isValid() ? `Обновлено: ${parsed.format('HH:mm')}` : ''
}

/**
 * «Сегодня» у салона, а не у браузера администратора: проект может быть в
 * другом поясе, и в 23:30 по Москве во Владивостоке уже завтра. Пояс хранится
 * либо как `UTC+3`, либо именем зоны — разбираем оба варианта.
 */
export function projectToday(projectTimezone?: string | null): string {
    const timezone = projectTimezone || 'Europe/Moscow'

    const now = new Date(Date.now())

    const utcOffset = /^UTC([+-]\d{1,2})$/.exec(timezone)
    if (utcOffset) {
        return new Date(now.getTime() + Number(utcOffset[1]) * 3600000).toISOString().slice(0, 10)
    }

    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(now)
        const value = (type: string) => parts.find(part => part.type === type)!.value
        return `${value('year')}-${value('month')}-${value('day')}`
    } catch (error) {
        return now.toISOString().slice(0, 10)
    }
}

export function addDays(date: string, days: number): string {
    return moment(date, DATE_FORMAT)
        .add(days, 'day')
        .format(DATE_FORMAT)
}

/** «10:30» → 630. Нечитаемое время — NaN. */
export function toMinutes(time: string | null | undefined): number {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(time ?? ''))
    return match ? Number(match[1]) * 60 + Number(match[2]) : NaN
}

/** 630 → «10:30». */
export function fromMinutes(value: number): string {
    const total = Math.max(0, Math.round(value))
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function initials(name: string | null): string {
    const parts = String(name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
    if (!parts.length) return '—'
    return parts
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
}

/**
 * Длительность того, что ставим в окно. При переносе клиент приходит со
 * своей записью целиком — её длительность, а не услуги из каталога.
 */
export function bookingLength(candidate: HotWindowCandidate | null, service: HotWindowService): number {
    const recorded = candidate?.source === 'transfer' ? candidate.futureRecord?.durationMinutes : 0
    return recorded && recorded > 0 ? recorded : service.durationMinutes
}

/** Допустимые начала: вся запись должна поместиться в окно. */
export function startBounds(window: HotWindow, length: number): { min: number; max: number } {
    const min = toMinutes(window.time)
    const max = toMinutes(window.timeTo) - length
    return { min, max: Math.max(min, max) }
}

/**
 * Предлагаемое начало. Для переноса — как можно ближе к привычному времени
 * записи клиента, для клиента без записи — начало окна.
 */
export function preferredStart(
    candidate: HotWindowCandidate | null,
    window: HotWindow,
    length: number
): number {
    const { min, max } = startBounds(window, length)
    const booked = candidate?.source === 'transfer' ? toMinutes(candidate.futureRecord?.time) : NaN
    if (!Number.isFinite(booked)) return min
    return Math.max(min, Math.min(booked, max))
}

/**
 * Почему клиент в списке — одна строка, как `reason()` в макете. Для
 * переноса важно, насколько сдвигается привычное время; для клиента без
 * записи — был ли он на этой услуге и где он относительно цикла.
 */
export function candidateReason(
    candidate: HotWindowCandidate,
    { start, referenceDate }: { start: number | null; referenceDate: string }
): string {
    if (candidate.source === 'transfer') {
        const booked = toMinutes(candidate.futureRecord?.time)
        if (!Number.isFinite(booked) || start === null) return candidate.sourceDetail || 'Перенос записи'
        const distance = Math.abs(booked - start)
        return distance === 0 ? 'Сохраняем привычное время записи' : `Сдвиг времени на ${formatDuration(distance)}`
    }

    return baseReason(candidate, dueInDays(candidate.nextVisitFcDate, referenceDate))
}

/**
 * Клиент без записи: текст причины — готовый `source_detail` функции, как
 * есть. Свой расчёт по дате прогноза — только если функция текст не отдала.
 */
function baseReason(candidate: HotWindowCandidate, due: number | null): string {
    const detail = candidate.sourceDetail?.trim()
    if (detail) return detail

    const isNew = !candidate.visitsService
    if (isNew) return 'Новая услуга · пора в салон'
    return due !== null && due <= 0 ? 'Пора повторить услугу' : 'Уже был на этой услуге'
}

function dueInDays(forecast: string | null, referenceDate: string): number | null {
    if (!forecast) return null
    const date = moment(forecast, DATE_FORMAT)
    const reference = moment(referenceDate, DATE_FORMAT)
    return date.isValid() && reference.isValid() ? date.diff(reference, 'day') : null
}

/** «12 визитов · постоянный» — постоянным считаем от пяти визитов, как в макете. */
export function visitsLine(candidate: HotWindowCandidate): string {
    return `${visitsLabel(candidate.totalVisits)}${candidate.totalVisits >= 5 ? ' · постоянный' : ''}`
}

export function sourceTitle(candidate: HotWindowCandidate): string {
    return candidate.source === 'base' ? 'Сейчас' : 'Текущая запись'
}

/**
 * «2 октября, 11:00 · Анна К.». Функция переносит запись только к тому же
 * мастеру на ту же услугу, поэтому мастер текущей записи — мастер окна.
 */
export function sourceDetail(candidate: HotWindowCandidate, master?: string): string {
    if (candidate.source === 'base') return 'Без будущей записи'
    const record = candidate.futureRecord
    if (!record) return candidate.sourceDetail
    const when = `${formatShortDate(record.visitDate)}, ${record.time}`
    return master ? `${when} · ${master}` : when
}

export function offerButtonLabel(taskType: HotWindowsTaskType): string {
    return taskType === 'transfer' ? 'Предложить перенос' : 'Предложить окно'
}

export function dayWord(dayKey: DayKey): string {
    return dayKey === 'today' ? 'сегодня' : 'завтра'
}

/** Текст предложения — формулировки макета, время — выбранное начало. */
export function composeOfferMessage({
    candidate,
    window,
    service,
    start,
    dayKey,
}: {
    candidate: HotWindowCandidate
    window: HotWindow
    service: HotWindowService
    start: number
    dayKey: DayKey
}): string {
    const firstName = String(candidate.name ?? '').trim().split(/\s+/)[0]
    const greeting = firstName ? `${firstName}, добрый день!` : 'Добрый день!'
    const at = fromMinutes(start)

    const day = dayWord(dayKey)

    // Тексты четырёх сценариев — из макета v12: приглашение и перенос,
    // каждое на сегодня и на завтра.
    if (candidate.source === 'transfer') {
        return [
            greeting,
            `У ${window.master} есть время ${day} в ${at}.`,
            `Предлагаем перенести вашу запись «${service.name}» на это время.`,
            `Вам может быть удобно прийти ${day}?`,
        ].join('\n')
    }
    return [
        greeting,
        `У ${window.master} ${day} есть время в ${at} на услугу «${service.name}».`,
        dayKey === 'today' ? 'Хотели бы прийти сегодня?' : 'Вы хотели бы записаться на это время?',
    ].join('\n')
}

/**
 * Кандидатов в карточке дня считаем по уникальным клиентам за день: один
 * клиент подходит на несколько окон и услуг, но позвать его можно один раз.
 */
export function uniqueCandidateCount(data: HotWindowsData | null | undefined): number {
    const clients = new Set<string>()
    for (const window of data?.windows ?? []) {
        for (const variant of window.variants) {
            for (const candidate of variant.candidates) clients.add(candidate.clientId)
        }
    }
    return clients.size
}

/** Цена «от» — по ней фильтр «Цена от» и сортировка каталога. */
export function catalogPrice(service: CatalogService): number | null {
    return service.priceMin ?? service.priceMax
}

export function passesMinPrice(service: CatalogService, minPrice: number): boolean {
    const price = catalogPrice(service)
    return minPrice <= 0 || (price !== null && price >= minPrice)
}

/** Каталог «по цене: от высокой к низкой», при равной цене — по названию. */
export function sortCatalog(services: CatalogService[]): CatalogService[] {
    const price = (service: CatalogService) => catalogPrice(service) ?? -Infinity
    return services.slice().sort((a, b) => price(b) - price(a) || a.name.localeCompare(b.name, 'ru'))
}

/** Набор по умолчанию — основные услуги, как в бэке. */
export function defaultServiceIds(services: CatalogService[]): string[] {
    return services.filter(service => service.type === 'main').map(service => service.id)
}

/** «от 1 200 ₽», «1 500 ₽» или «Цена не указана» — строка каталога. */
export function catalogPriceLabel(service: CatalogService): string {
    const { priceMin, priceMax } = service
    if (priceMin === null && priceMax === null) return 'Цена не указана'
    if (priceMin !== null && priceMax !== null && priceMax > priceMin) return `от ${formatMoney(priceMin)}`
    return formatMoney((priceMin ?? priceMax) as number)
}

export function catalogTypeName(service: CatalogService): string {
    return service.type === 'additional' ? 'Дополнительная' : 'Основная'
}

/** «Выбрано услуг: 3 · от 1 000 ₽» — строка активных фильтров. */
/**
 * «Услуги: 26 услуг · Цена от 1 000 ₽» — строка «Параметров фильтра», как в
 * макете v12. Без выбора в фильтре — все услуги набора; до трёх услуг —
 * названиями.
 */
export function filterSummary(
    filters: HotWindowsFilters,
    catalog: CatalogService[] = [],
    allowedIds: string[] = []
): string {
    const ids = filters.serviceIds.length ? filters.serviceIds : allowedIds
    const byId = new Map(catalog.map(service => [service.id, service.name]))
    const names = ids.map(id => byId.get(id)).filter(Boolean) as string[]
    const services = !ids.length
        ? 'не выбраны'
        : names.length === ids.length && ids.length <= 3
        ? names.join(', ')
        : `${ids.length} ${plural(ids.length, 'услуга', 'услуги', 'услуг')}`
    const price = filters.minPrice > 0 ? `Цена от ${formatMoney(filters.minPrice)}` : 'Без порога цены'
    return `Услуги: ${services} · ${price}`
}

export function activeFilterCount(filters: HotWindowsFilters): number {
    return Number(filters.serviceIds.length > 0) + Number(filters.minPrice > 0)
}

const FILTERS_STORAGE_PREFIX = 'dh-hot-windows-filters:'

/**
 * Фильтры экрана сохраняются в браузере по проекту: без этого они
 * сбрасывались при каждом заходе. Хранилище может быть недоступно
 * (приватный режим) — тогда просто без фильтров.
 */
export function loadSavedFilters(projectId: string, storage: Pick<Storage, 'getItem'> | null = safeStorage()): HotWindowsFilters {
    const empty: HotWindowsFilters = { serviceIds: [], minPrice: 0 }
    try {
        const raw = JSON.parse(storage?.getItem(FILTERS_STORAGE_PREFIX + projectId) ?? 'null')
        const serviceIds = Array.isArray(raw?.serviceIds) ? raw.serviceIds.filter((id: unknown) => typeof id === 'string') : []
        const minPrice = Number(raw?.minPrice)
        return { serviceIds, minPrice: Number.isFinite(minPrice) && minPrice > 0 ? Math.round(minPrice) : 0 }
    } catch (error) {
        return empty
    }
}

export function saveFilters(
    projectId: string,
    filters: HotWindowsFilters,
    storage: Pick<Storage, 'setItem' | 'removeItem'> | null = safeStorage()
): void {
    try {
        if (!filters.serviceIds.length && !filters.minPrice) storage?.removeItem(FILTERS_STORAGE_PREFIX + projectId)
        else storage?.setItem(FILTERS_STORAGE_PREFIX + projectId, JSON.stringify(filters))
    } catch (error) {
        // хранилище недоступно — фильтр останется до перезагрузки
    }
}

function safeStorage(): Storage | null {
    try {
        return typeof window !== 'undefined' ? window.localStorage : null
    } catch (error) {
        return null
    }
}
